import type { UserRole } from '@/hooks/useCurrentUser';

/**
 * Etiqueta i18n de cada rol. Los identificadores del backend no coinciden con la etiqueta que
 * ve el usuario (`AdminGlobal` se muestra como "Admin General" y `AdminOU` como "Admin OU
 * Pulso") por decisión de 2026-07-28: se evitó migrar el campo `Roles` de la colección `users`.
 * Este mapa es el único puente entre ambos vocabularios; cualquier pantalla que muestre roles
 * debe usarlo en vez de rotular a mano.
 */
export const ROLE_LABEL_KEY: Record<UserRole, string> = {
  Employee:         'admin.roleEmployee',
  AdminGlobal:      'admin.roleAdminGeneral',
  AdminPulso:       'admin.roleAdminPulso',
  AdminOU:          'admin.roleAdminOUPulso',
  AdminBrillamos:   'admin.roleAdminBrillamos',
  AdminOUBrillamos: 'admin.roleAdminOUBrillamos',
  // Condicion calculada Manager: se muestra como un rol mas.
  Manager:            'admin.roleManager',
};

/** Clase CSS que da color a la etiqueta de cada rol (ver UsersAdminView.css). */
export const ROLE_BADGE_CLASS: Record<UserRole, string> = {
  Employee:         'empleado',
  AdminGlobal:      'admin-general',
  AdminPulso:       'admin-pulso',
  AdminOU:          'admin-ou-pulso',
  AdminBrillamos:   'admin-brillamos',
  AdminOUBrillamos: 'admin-ou-brillamos',
  // Condicion calculada Manager: se muestra como un rol mas.
  Manager:            'manager',
};

/**
 * Roles de administrador acotados a una rama del organigrama: exigen elegir una OU. Su
 * complemento —los administradores sin alcance de OU: `AdminGlobal`, `AdminPulso`,
 * `AdminBrillamos`— no necesita lista propia: el alcance se decide con un único predicado
 * (`hasOuScopedAdminRole`) porque un usuario puede tener a la vez un rol global y uno acotado, y
 * dos predicados independientes se contradicen en ese caso.
 */
export const OU_SCOPED_ADMIN_ROLES: readonly UserRole[] = ['AdminOU', 'AdminOUBrillamos'];

/**
 * True si alguno de los roles dados exige un alcance de OU. Basta un rol acotado para que la OU
 * siga aplicando, aunque el usuario tenga además un rol de alcance global.
 */
export function hasOuScopedAdminRole(roles: readonly UserRole[]): boolean {
  return roles.some(role => OU_SCOPED_ADMIN_ROLES.includes(role));
}

/**
 * Roles que administran el Programa de RECONOCIMIENTO sin acotarse a una rama: su alcance es el
 * programa completo. Es el espejo exacto de lo que ya decide el backend, en sus dos extremos:
 * `CampaignsController.RequireScope` sale temprano con `IsGlobal || IsAdminReconocimiento`, y
 * `AppUser.SetRecognitionScope` DEJA VACÍO `RecognitionScopeOuIds` para estos roles (nunca guarda
 * OUs para un rol de alcance global).
 */
export const UNSCOPED_RECOGNITION_ADMIN_ROLES: readonly UserRole[] = ['AdminGlobal', 'AdminBrillamos'];

/**
 * True si el actor administra Reconocimiento SIN acotarse a una OU.
 *
 * 🔴 Bug UAT #114 — aquí la regla de Pulso NO aplica: en Pulso «el rol acotado manda» sobre el
 * global (`hasOuScopedAdminRole`, D-040), pero en Reconocimiento eso deja al actor sin alcance
 * alguno. Un Admin Reconocimiento que además tenga un rol acotado (p. ej. Admin OU Pulso, que la
 * pantalla de Administradores de Reconocimiento CONSERVA a propósito) caía por la rama acotada y
 * leía `RecognitionScopeOuIds` — que para su rol el backend deja vacío por definición—, así que el
 * árbol de Alcance del wizard salía en blanco y no podía crear ninguna campaña. El backend sí lo
 * dejaba pasar: quien bloqueaba era la UI.
 */
export function hasUnscopedRecognitionRole(roles: readonly UserRole[]): boolean {
  return roles.some(role => UNSCOPED_RECOGNITION_ADMIN_ROLES.includes(role));
}
